import { useMemo } from 'react'
import {
  STANDING_LABEL,
  STANDINGS,
  dayWords,
  itemFacts,
  itemLabel,
  knowledgeGrid,
  wordsByStanding,
  type KnowledgeTile,
  type Standing,
} from '@aurora/core'
import { Sheet } from '../ui/Sheet'
import { StandingLegend, StandingPill } from './Standing'
import { useItemHistory, type Attempt, type useCards } from './useCards'

type Cards = ReturnType<typeof useCards>

/**
 * What she knows, from everything she has played: the letters and sounds as
 * a grid, and the words she has met, grouped by how well they hold. It is a
 * map of where she is, not a test result; nothing here compares her to
 * anyone, and a letter she has not met is simply not met yet.
 */
export function KnowsPanel({ cards, onItem }: { cards: Cards; onItem: (items: string[], title?: string) => void }) {
  const decks = cards.playable
  const grid = useMemo(() => knowledgeGrid(cards.memory, decks, cards.now), [cards.memory, decks, cards.now])
  const words = useMemo(() => wordsByStanding(cards.memory, decks, cards.now), [cards.memory, decks, cards.now])
  const counts = useMemo(() => {
    const out = Object.fromEntries(STANDINGS.map((s) => [s, 0])) as Record<Standing, number>
    for (const t of grid.letters) out[t.standing]++
    return out
  }, [grid])
  const metWords = STANDINGS.filter((s) => s !== 'new').reduce((n, s) => n + words[s].length, 0)
  const name = cards.child?.name ?? 'She'

  return (
    <div className="knows">
      <section className="knows-block reveal">
        <div className="knows-head">
          <h3 className="knows-title">Letters and their sounds</h3>
          <StandingLegend counts={counts} />
        </div>
        <p className="knows-key">
          The tile's colour is the letter. The line under it is the sound it makes, from her sounds games, her cards and the words she reads well.
        </p>
        <ul className="letter-grid">
          {grid.letters.map((t) => (
            <li key={t.key}>
              <Tile tile={t} onOpen={() => onItem(t.items, t.label)} />
            </li>
          ))}
        </ul>
        <ul className="letter-grid is-digraphs">
          {grid.digraphs.map((t) => (
            <li key={t.key}>
              <Tile tile={t} onOpen={() => onItem(t.items, t.key)} />
            </li>
          ))}
        </ul>
      </section>

      <section className="knows-block reveal" style={{ '--i': 1 } as React.CSSProperties}>
        <div className="knows-head">
          <h3 className="knows-title">Words</h3>
          <span className="knows-count tnum">{metWords ? `${metWords} met` : ''}</span>
        </div>
        {metWords === 0 ? (
          <p className="knows-empty">
            {/* Words land here when a game checks she reads one (a picture picked, a card answered); sliding through a word with the voice is kept as met, not as read. */}
            No words read yet. A word shows here once a game checks {name} reads it: picking its
            picture in blending, or answering it in sight words and her decks.
          </p>
        ) : (
          <div className="word-groups">
            {[...STANDINGS].reverse().filter((s) => s !== 'new' && words[s].length).map((s) => (
              <div key={s} className="word-group">
                <p className="word-group-label">
                  <span className={`standing-swatch is-${s}`} aria-hidden="true" /> {STANDING_LABEL[s]}
                  <span className="tnum subtle"> {words[s].length}</span>
                </p>
                <ul className="word-chips">
                  {words[s].map((w) => (
                    <li key={w.word}>
                      <button className={`word-chip is-${s}`} onClick={() => onItem(w.items, w.word)}>
                        {w.word}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

/** One spelling: the tile wears how she knows the letter, the line under it how she knows its sound. */
function Tile({ tile, onOpen }: { tile: KnowledgeTile; onOpen: () => void }) {
  const sound = tile.sound
  return (
    <button
      className={`letter-tile is-${tile.standing}`}
      onClick={onOpen}
      aria-label={`${tile.label}: letter ${STANDING_LABEL[tile.standing]}${sound ? `, sound ${STANDING_LABEL[sound]}` : ''}`}
    >
      <span className="letter-tile-glyph">{tile.label}</span>
      {sound && <span className={`letter-tile-sound is-${sound}`} aria-hidden="true" />}
    </button>
  )
}

/**
 * One item, told plainly: how it stands, how often she has met it, when it
 * comes back, and every attempt behind that. A tile can gather several items
 * (the letter card, its sound in blending), and each gets its own line,
 * because "she knows the card but not the sound" is exactly what a parent
 * wants to see.
 */
export function ItemSheet({ items, title, cards, onClose }: { items: string[]; title?: string; cards: Cards; onClose: () => void }) {
  const decks = cards.playable
  const history = useItemHistory(cards.child?._id, items)
  const heading = title ?? itemLabel(items[0] ?? '', decks).title

  return (
    <Sheet open onClose={onClose} eyebrow="What she knows" title={heading}>
      <div className="item-sheet">
        {items.map((key) => {
          const label = itemLabel(key, decks)
          const f = itemFacts(cards.memory[key], cards.now)
          const tries = (history ?? []).filter((a) => a.item === key)
          return (
            <section key={key} className="item-row">
              <header className="item-row-head">
                <div>
                  <p className="item-row-title">{label.title}</p>
                  <p className="item-row-where">{label.where}</p>
                </div>
                <StandingPill standing={f.standing} />
              </header>
              {f.seen === 0 ? (
                <p className="item-row-none">Not met yet. It comes when its deck or game reaches it.</p>
              ) : (
                <>
                  <dl className="item-facts tnum">
                    <Fact k="Met" v={`${f.seen} ${f.seen === 1 ? 'time' : 'times'}`} />
                    <Fact k="Right" v={`${f.right} of ${f.seen}`} />
                    {f.streak > 1 && <Fact k="In a row" v={String(f.streak)} />}
                    {f.lastAt && <Fact k="Last" v={dayWords(f.lastAt, cards.now, cards.zone)} />}
                    {f.nextAt && <Fact k="Comes back" v={f.nextAt <= cards.now ? 'due now' : dayWords(f.nextAt, cards.now, cards.zone)} />}
                    {f.ms && <Fact k="Usually" v={`${(f.ms / 1000).toFixed(1)}s`} />}
                  </dl>
                  <History tries={tries} loading={history === undefined} zone={cards.zone} now={cards.now} />
                </>
              )}
            </section>
          )
        })}
      </div>
    </Sheet>
  )
}

function Fact({ k, v }: { k: string; v: string }) {
  return (
    <div className="item-fact">
      <dt>{k}</dt>
      <dd>{v}</dd>
    </div>
  )
}

const MODE: Record<string, string> = { read: 'read aloud', choose: 'picked it', listen: 'found it by ear', blend: 'blended it', build: 'built it', recall: 'remembered it' }

/** Every attempt, oldest to newest as dots, then the latest few in words. */
function History({ tries, loading, zone, now }: { tries: Attempt[]; loading: boolean; zone: string; now: number }) {
  if (loading) return <div className="item-history is-loading skeleton" />
  if (!tries.length) return null
  const dots = [...tries].reverse().slice(-24)
  return (
    <div className="item-history">
      <div className="item-dots" aria-label={`${tries.filter((t) => t.ok).length} right of the last ${tries.length}`}>
        {dots.map((t, i) => (
          <span key={i} className={`item-dot${t.ok ? ' is-ok' : ''}${t.help ? ' is-helped' : ''}`} title={new Date(t.at).toLocaleString()} />
        ))}
      </div>
      <ul className="item-tries">
        {tries.slice(0, 5).map((t, i) => (
          <li key={i}>
            <span className="item-try-when">{dayWords(t.at, now, zone)}</span>
            <span className="item-try-what">
              {t.ok ? (t.grade === 4 ? 'Easy' : 'Got it') : 'Not yet'}
              {t.mode && MODE[t.mode] ? `, ${MODE[t.mode]}` : ''}
              {t.help ? ', with help' : ''}
            </span>
            {t.ok && t.ms ? <span className="item-try-ms tnum">{(t.ms / 1000).toFixed(1)}s</span> : null}
          </li>
        ))}
      </ul>
    </div>
  )
}
